import { rem } from 'polished'
import type * as React from 'react'
import styled, { css } from 'styled-components'

import type { CalendarSpanSegment } from './Calendar.types'
import CalendarSpanBar from './CalendarSpanBar'
import LineBedIcon from 'LuxKit/Icons/line/LineBedIcon'
import LineBusIcon from 'LuxKit/Icons/line/LineBusIcon'
import LinePlaneIcon from 'LuxKit/Icons/line/LinePlaneIcon'
import LineShipIcon from 'LuxKit/Icons/line/LineShipIcon'
import LineTicketIcon from 'LuxKit/Icons/line/LineTicketIcon'

import type { StopCalendarChip, StopCalendarSpanData, TransportMarker } from './stopCalendarOverlays'

export const STOP_BAR_LABEL_COLOUR = 'var(--palette-neutral-surface-dark-contrast)'

/**
 * How loudly a bar should speak.
 *
 * `muted` is for the bars that are only there as context — the rest of the
 * trip shown behind a date choice being made about one stop. They keep their
 * colour so the itinerary is still legible at a glance, but step back far
 * enough that the stop actually being edited reads as the subject.
 */
export type StopCalendarSpanEmphasis = 'normal' | 'muted'

/**
 * How much room to leave where two stops change over on the same day.
 *
 * `handle` keeps the two drag handles' circular outlines clear of each other,
 * which costs 10px from each bar. Without handles to separate that clearance
 * is 20px of dead space in the middle of a continuous trip, and reads as the
 * itinerary being broken into disconnected pieces — `tight` leaves just
 * enough of a seam to tell the two stops apart.
 */
export type StopCalendarBoundaryGap = 'handle' | 'tight'

/** Per-side inset at a shared changeover day when no handles need clearing. */
const TIGHT_BOUNDARY_GAP_PX = 2

/**
 * The stop's name, ready for the bar to drop straight in.
 *
 * Wrapped here rather than passed as bare text so it can ellipsise: a bar is
 * only as wide as the days it covers, and "Melbou" reads as a rendering fault
 * where "Melbo…" reads as a name that did not fit. The full name stays on the
 * element for hover.
 */
const BarLabel = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const emphasised = css<{ $emphasis: StopCalendarSpanEmphasis }>`
  ${props => props.$emphasis === 'muted' && css`
    opacity: 0.55;
  `}
`

/**
 * The bar dims and lightens a label that is not on the span's first week, to
 * mark it as continued. A name deliberately placed on a later week is the
 * span's only name, so that treatment is undone — and pushed clear of the
 * mask fading that edge, which would otherwise wash out its first letter.
 */
const labelledContinuation = css`
  &.has-stop-label > div > span {
    opacity: 1;
    font-weight: 600;
  }

  &.has-stop-label.fade-left,
  &.has-stop-label.fade-both {
    padding-inline-start: ${rem(15)};
  }
`

const StopBar = styled(CalendarSpanBar)<{ $emphasis: StopCalendarSpanEmphasis }>`
  ${emphasised}
  ${labelledContinuation}
`

/**
 * The pill treatment for a single item on the trip — a booked hotel over its
 * stay, or the transport carrying someone between two stops. Rounded and
 * outlined so it reads as one booking sitting under the stop it belongs to,
 * rather than as another stop.
 */
const ItemBar = styled(CalendarSpanBar)<{ $emphasis: StopCalendarSpanEmphasis }>`
  ${emphasised}
  ${labelledContinuation}
  border: 1px solid currentColor;
  font-size: ${rem(10.5)};
  font-weight: 500;

  &.starts {
    border-start-start-radius: ${rem(999)};
    border-end-start-radius: ${rem(999)};
  }

  &.ends {
    border-start-end-radius: ${rem(999)};
    border-end-end-radius: ${rem(999)};
  }
`

/*
 * LuxKit has no rail icon, so a train falls back to the generic ticket rather
 * than borrowing something that means another mode.
 */
function getChipIcon(itemType: StopCalendarChip['itemType']) {
  switch (itemType) {
    case 'ACCOMMODATION': return <LineBedIcon size="XS" />
    case 'FLIGHT': return <LinePlaneIcon size="XS" />
    case 'CRUISE':
    case 'FERRY': return <LineShipIcon size="XS" />
    case 'BUS': return <LineBusIcon size="XS" />
    default: return <LineTicketIcon size="XS" />
  }
}

interface SpanBarProps {
  segment: CalendarSpanSegment<StopCalendarSpanData>
  emphasis?: StopCalendarSpanEmphasis
  /** @default 'handle' */
  boundaryGap?: StopCalendarBoundaryGap
  /** Optional drag affordances at the span's real first and last day. */
  startHandle?: React.ReactNode
  endHandle?: React.ReactNode
}

/**
 * One week's slice of a stop, or of a hotel booked inside one.
 *
 * Shared by every calendar that draws the itinerary — the stop date editor
 * and the read-only trip context on a search calendar — so a stop looks the
 * same wherever someone meets it.
 */
export function StopCalendarSpanBar(props: SpanBarProps) {
  const { segment, emphasis = 'normal', boundaryGap = 'handle', startHandle, endHandle } = props
  const spanData = segment.span.data
  const itemSpan = spanData && 'kind' in spanData ? spanData : undefined
  const transportSpan = itemSpan?.kind === 'transport' ? itemSpan : undefined
  const stopSpan = spanData && !('kind' in spanData) ? spanData : undefined
  /*
   * A tight boundary is drawn as a plain inset rather than by asking the bar
   * for its shared-boundary treatment, because that treatment's gap is sized
   * for the handles it exists to separate.
   */
  const isTightBoundary = boundaryGap === 'tight'
  const hasSharedStartBoundary = !isTightBoundary && !!stopSpan?.hasSharedStartBoundary
  const hasSharedEndBoundary = !isTightBoundary && !!stopSpan?.hasSharedEndBoundary
  const SpanBar = itemSpan ? ItemBar : StopBar

  /*
   * Two stops changing over on the same day now meet exactly at its midpoint,
   * so all a seam has to do is tell them apart. Without handles to clear, that
   * is a couple of pixels — the wider handle gap is applied by the bar itself.
   */
  const startInsetPx = isTightBoundary && stopSpan?.hasSharedStartBoundary ? TIGHT_BOUNDARY_GAP_PX : 0
  const endInsetPx = isTightBoundary && stopSpan?.hasSharedEndBoundary ? TIGHT_BOUNDARY_GAP_PX : 0

  // The name goes on the widest slice, which is not always the one the stop
  // begins in. Decided in buildSpanLayout, where the siblings are visible.
  const carriesLabel = segment.isWidest
  const name = itemSpan?.label ?? stopSpan?.placeName
  const background = itemSpan
    ? itemSpan.colours.background
    : spanData?.colours.foreground ?? '#0F4C4C'
  const foreground = itemSpan
    ? itemSpan.colours.foreground
    : STOP_BAR_LABEL_COLOUR

  return <SpanBar
    $emphasis={emphasis}
    // Every slice of a span, not just the one carrying the name.
    data-testid={`calendar-bar-${segment.span.id}`}
    className={carriesLabel ? 'has-stop-label' : undefined}
    columnStart={segment.columnStart}
    columnSpan={segment.columnSpan}
    isStart={segment.isStart}
    isEnd={segment.isEnd}
    lane={segment.lane}
    /*
     * Every bar runs from the middle of its first day to the middle of its
     * last, which is how a stay actually works: nobody arrives at midnight,
     * and a stay ending on the 9th is gone by the middle of it. Two stops
     * changing over on the same day therefore each take the half of it they
     * are really there for, rather than both claiming the whole tile.
     */
    startInsetPx={startInsetPx}
    endInsetPx={endInsetPx}
    hasSharedStartBoundary={hasSharedStartBoundary}
    hasSharedEndBoundary={hasSharedEndBoundary}
    background={background}
    foreground={foreground}
    // Name and icon travel together to whichever week has room for them.
    icon={itemSpan && carriesLabel
      ? (transportSpan ? getChipIcon(transportSpan.itemType) : <LineBedIcon size="XS" />)
      : undefined}
    label={carriesLabel && name
      ? <BarLabel title={name} data-testid={`calendar-span-${segment.span.id}`}>{name}</BarLabel>
      : undefined}
    startHandle={startHandle}
    endHandle={endHandle}
  />
}

const Markers = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: ${rem(3)};
  margin-top: ${rem(3)};
`

/**
 * A leg that starts and finishes inside one day, drawn on that day rather than
 * across it. Half a tile either side of the midpoint would be a sliver nobody
 * can read, and a whole tile would claim a night that is not spent travelling.
 */
const Marker = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${rem(18)};
  height: ${rem(18)};
  border-radius: ${rem(999)};
  border: 1px solid currentColor;
`

interface DayMarkersProps {
  markers: ReadonlyArray<TransportMarker>
}

/** The journeys that happen on a day without spanning it. */
export function StopCalendarDayMarkers({ markers }: DayMarkersProps) {
  if (!markers.length) return null

  return <Markers data-transport-day-markers>
    {markers.map(marker => (
      <Marker
        key={marker.id}
        data-testid={`calendar-marker-${marker.id}`}
        style={{
          backgroundColor: marker.colours.background,
          color: marker.colours.foreground,
        }}
        title={marker.label}
      >
        {getChipIcon(marker.itemType)}
      </Marker>
    ))}
  </Markers>
}
